'use client';

import { useEffect, useState } from 'react';
import { Autocomplete, AutocompleteItem, Button } from '@heroui/react';
import { devLoginAction } from '@/lib/devLoginAction';
import { getApiBaseUrl } from '@/lib/auth-client';

export function MasterLogin() {
  const [company, setCompany] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<string>('');

  useEffect(() => {
    const fetchCompany = async () => {
      const apiUrl = `${getApiBaseUrl()}/company`;

      try {
        console.log('Fetching companies from:', apiUrl);

        const response = await fetch(apiUrl, {
          headers: {
            'api-key': process.env.NEXT_PUBLIC_SHI_DEV_BETA_API_KEY as string,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setCompany(data);
      } catch (error) {
        console.error('Error fetching companies:', error);
      }
    };

    fetchCompany();
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!selectedCompany) return;

      const apiUrl = `${getApiBaseUrl()}/user?_companyId=${selectedCompany}`;

      try {
        const response = await fetch(apiUrl, {
          headers: {
            'api-key': process.env.NEXT_PUBLIC_SHI_DEV_BETA_API_KEY as string,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setUsers(data);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    fetchUsers();
  }, [selectedCompany]);

  const switchUser = async (userId: string) => {
    const selectedUserData = users.find((user) => user._id === userId);

    if (!selectedUserData) return console.log('No user data found');

    try {
      const result = await devLoginAction(selectedUserData.email);

      if ('error' in result) {
        console.error('Dev login failed:', result.error);
        return;
      }

      window.location.reload();
    } catch (error) {
      console.error('Failed to switch user:', error);
    }
  };

  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  const autocompleteProps = {
    variant: 'bordered' as const,
    size: 'sm' as const,
    className: 'w-full',
    inputProps: {
      classNames: {
        inputWrapper:
          'border-[#2e2e2e] bg-[#1a1a1a] data-[hover=true]:border-[#3a3a3a] group-data-[focus=true]:border-primary',
      },
    },
    popoverProps: { className: 'dark text-foreground' },
  };

  return (
    <div className='dark mt-8 flex flex-col gap-3 rounded-lg border border-dashed border-[#2e2e2e] p-4 text-foreground lg:mt-10'>
      <span className='self-start rounded bg-primary/15 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-primary'>
        Dev login
      </span>
      <Autocomplete
        {...autocompleteProps}
        label='Company'
        placeholder='Search for a company'
        selectedKey={selectedCompany}
        onSelectionChange={(value) => {
          setSelectedCompany(value?.toString() || '');
          setSelectedUser('');
        }}
      >
        {company.map((comp) => (
          <AutocompleteItem key={comp._id}>{comp.name}</AutocompleteItem>
        ))}
      </Autocomplete>

      <Autocomplete
        {...autocompleteProps}
        label='User'
        placeholder='Search for a user'
        selectedKey={selectedUser}
        isDisabled={users.length === 0}
        onSelectionChange={(value) => {
          setSelectedUser(value?.toString() || '');
        }}
      >
        {users.map((user) => (
          <AutocompleteItem
            key={user._id}
            textValue={`${user.firstName} ${user.lastName}`}
          >
            {user.firstName} {user.lastName} ({user.email})
          </AutocompleteItem>
        ))}
      </Autocomplete>

      <Button
        className='h-10 rounded-lg font-bold uppercase'
        color='primary'
        variant='flat'
        isDisabled={!selectedUser}
        onPress={() => switchUser(selectedUser)}
      >
        Switch user
      </Button>
    </div>
  );
}
